import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TRACK_COLORS } from "config/constants";
import { Routing } from "config/enums/routings";
import equal from "fast-deep-equal";
import { useTranslation } from "react-i18next";
import { setCesiumMode } from "stores/genericStore/actions";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import { useOptions } from "stores/optionsStore/OptionsContext";
import {
  rewriteComputedTrackpoints,
  rewriteTrackmeta,
  rewriteTrackWaypoints,
} from "stores/routingStore/actions";
import { isEditedTrackSaved } from "stores/routingStore/getTrackData";
import { useRouting } from "stores/routingStore/RoutingContext";
import { SavingStatus, TrackWithMetadata, TrackRaw, Track } from "types/app";
import { getUrlsIfAreValidImages } from "utils/api/checkIfUrlIsImage";
import { postToApi } from "utils/api/postToApi";
import {
  parseTrackForAPI,
  parseTrackFromAPI,
} from "utils/helpers/trackParser/trackParser";
import { showTrackDeletionSuccessNotification } from "utils/notifications/trackNotifications";
import { getTracksWithMetadata } from "utils/tracks/getTracksWithMetadata";
import { useAuth } from "./useAuth";
import { useEditMode } from "./useEditMode";
import { useGenericModals } from "./useGenericModals";
import { useHiddenIds } from "./useHiddenIds";
import { useMapContext } from "./useMapContext";
import { showErrorNotification } from "utils/notifications/customNotifications";
import { useImageUpload } from "./useImageUpload";

const DEFAULT_NEW_TRACK_DATA: Partial<Track> = {
  id: -1,
  name: "Track",
  desc: "",
  links: [],
  routing: Routing.auto,
  pub: false,
  trackPointsEle: [],
};

export type CreateTrackInterface = Omit<
  Partial<Track>,
  "id" | "userId" | "uuid"
>;
export type UpdateTrackInterface = Partial<CreateTrackInterface>;

interface UserTrackData {
  tracks: TrackWithMetadata[] | null;
  hiddenTrackIds: number[];
  selectedTrack: TrackWithMetadata | null;
  selectTrack: (id: number | null) => void;
  /**
   * Throws when the track couldn't be saved. `trackStatus` follows the saving of the edited track (or a new one),
   * not of the other tracks (e.g. when they're hidden or renamed)
   */
  updateTrack: (id: number, updatedData: UpdateTrackInterface) => Promise<void>;
  deleteTrack: (id: number | number[]) => Promise<unknown>;
  /** Throws when the track couldn't be saved */
  createTrack: (data: CreateTrackInterface) => Promise<void>;
  toggleTrackVisibility: (trackId: number) => void;
  /**
   * Runs `leave` (which closes or replaces the own track open in the editor) right away when the track is saved,
   * otherwise only after the user confirms losing its latest change. Returns whether it ran right away.
   */
  leaveOpenTrack: (leave: () => void) => boolean;
  isFetching: boolean;
  fetchTracks: () => Promise<unknown>;
  trackStatus: SavingStatus;
  getImagesFromLinks: () => Promise<string[]>;
  getSelectedTrackImages: () => Promise<string[]>;
}

const UserTrackContext = React.createContext<UserTrackData>({
  tracks: null,
  hiddenTrackIds: [],
  selectedTrack: null,
  selectTrack: () => null,
  updateTrack: () => Promise.resolve(),
  deleteTrack: () => Promise.resolve(null),
  createTrack: () => Promise.resolve(),
  isFetching: false,
  fetchTracks: () => Promise.resolve(null),
  trackStatus: "unsaved",
  getImagesFromLinks: () => Promise.resolve([]),
  getSelectedTrackImages: () => Promise.resolve([]),
  toggleTrackVisibility: () => undefined,
  leaveOpenTrack: (leave) => {
    leave();
    return true;
  },
});

type GetTracksResponse = {
  tracks: TrackRaw[];
};

export const useProvideUserTrackData = (): UserTrackData => {
  const [isFetching, setIsFetching] = useState(false);
  const [tracks, setTracks] = useState<TrackWithMetadata[] | null>(null);
  const { hiddenIds: hiddenTrackIds, setHiddenFrom, toggle } = useHiddenIds();
  const [selectedTrackId, setSelectedTrackId] = useState<number | null>(null);
  const [trackStatus, setTrackStatus] = useState<SavingStatus>("unsaved");
  const {
    openConfirmLosingTrackModal,
    openConfirmLosingChangesModal,
    openErrorModal,
  } = useGenericModals();
  const genericDispatch = useGenericDispatch();
  const { updateStorageSize } = useImageUpload();
  const { editMode, setEditMode } = useEditMode();
  const shouldSelectLastTrack = useRef(false);
  const {
    state: routingState,
    dispatch,
    history: { clearHistory },
    setEditedTrack,
    openedTrack,
    setOpenedTrack,
  } = useRouting();
  const {
    state: { language },
  } = useOptions();
  const { map } = useMapContext();
  const { user } = useAuth();
  const { t } = useTranslation();
  const tracksRef = useRef(tracks);
  const selectedTrackIdRef = useRef(selectedTrackId);
  const editModeRef = useRef(editMode);
  const routingStateRef = useRef(routingState);
  const trackStatusRef = useRef(trackStatus);

  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  useEffect(() => {
    routingStateRef.current = routingState;
  }, [routingState]);

  useEffect(() => {
    trackStatusRef.current = trackStatus;
  }, [trackStatus]);

  useEffect(() => {
    selectedTrackIdRef.current = selectedTrackId;
  }, [selectedTrackId]);

  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  // Signed out
  useEffect(() => {
    if (user) return;
    shouldSelectLastTrack.current = false;
    setIsFetching(false);
    setTracks(null);
    setHiddenFrom(null);
    setSelectedTrackId(null);
    tracksRef.current = null;
    selectedTrackIdRef.current = null;
    setTrackStatus("unsaved");
  }, [setHiddenFrom, user]);

  // The tracks are fetched when the user signs in, not again when the user's settings switch the language
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const fetchTracks = useCallback(async () => {
    if (!user) return Promise.reject(new Error("not_signed_in"));
    try {
      const data = await postToApi<GetTracksResponse>(
        "get-tracks",
        languageRef.current
      );

      if (!equal(tracksRef.current, data.tracks)) {
        const decodedTracks = data.tracks.map(parseTrackFromAPI);
        const tracksWithMeta = getTracksWithMetadata(decodedTracks);

        setTracks(tracksWithMeta);
        setHiddenFrom(tracksWithMeta);
      }
      return data.tracks;
    } catch (_error) {
      setTracks(null);
      setHiddenFrom(null);
      return console.error("error getting tracks");
    }
  }, [setHiddenFrom, user]);

  const selectTrack = useCallback(
    (id: number | null) => {
      genericDispatch(setCesiumMode(false));
      if (id === null) {
        setSelectedTrackId(null);
        setEditedTrack(null);
        return;
      }
      if (!tracksRef.current) throw new Error("Tracks empty");
      const foundTrack = tracksRef.current.find((track) => track.id === id);
      if (!foundTrack) throw new Error("Track not found");

      const select = () => {
        dispatch(
          rewriteTrackmeta({
            totalDistance: undefined,
            trackPointsSpeed: undefined,
            trackPointsTime: undefined,
            totalTime: undefined,
          })
        );
        setSelectedTrackId(foundTrack.id);
        setEditedTrack(foundTrack, map);
        setEditMode("track");
        setTrackStatus("saved");
      };

      if (editModeRef.current === "track" && !selectedTrackIdRef.current) {
        openConfirmLosingTrackModal({ onConfirm: select });
        return;
      }
      select();
    },
    [
      dispatch,
      genericDispatch,
      map,
      openConfirmLosingTrackModal,
      setEditMode,
      setEditedTrack,
    ]
  );

  const updateTrack = useCallback(
    async (id: number, newData: UpdateTrackInterface) => {
      if (!user) throw new Error("not_signed_in");

      const savedTrack =
        id === -1
          ? DEFAULT_NEW_TRACK_DATA
          : tracksRef.current?.find((track) => track.id === id);
      // Saved without its data and id, it would be created as a new track
      if (!savedTrack) throw new Error("Track not found");

      const isEditedTrack = id === -1 || id === selectedTrackIdRef.current;
      if (isEditedTrack) setTrackStatus("saving");

      try {
        await postToApi(
          "update-track",
          language,
          parseTrackForAPI({ ...savedTrack, ...newData })
        );
        await fetchTracks();
        if (isEditedTrack) setTrackStatus("saved");
      } catch (error) {
        if (isEditedTrack) setTrackStatus("unsaved");
        // Callers rely on this to tell the user and to roll back optimistic updates
        throw error;
      }
    },
    [fetchTracks, language, user]
  );

  const deleteTrack = useCallback(
    async (id: number | number[]) => {
      const ids = Array.isArray(id) ? id : [id];
      const deletedName =
        ids.length === 1
          ? tracksRef.current?.find((track) => track.id === ids[0])?.name
          : undefined;

      try {
        await postToApi("delete-track", language, {
          id: Array.isArray(id) ? undefined : id,
          ids: Array.isArray(id) ? id : undefined,
        });
      } catch (_error) {
        openErrorModal({
          title: t("generic.error_title"),
          text: t("errors.error_deleting_track.text"),
        });
        return;
      }

      // The editor is emptied only when the track open in it was deleted (not e.g. a new track being drawn)
      if (
        selectedTrackIdRef.current !== null &&
        ids.includes(selectedTrackIdRef.current)
      ) {
        clearHistory();
        dispatch(
          [rewriteTrackWaypoints([]), rewriteComputedTrackpoints([])],
          false
        );
        setSelectedTrackId(null);
      }

      if (deletedName) {
        showTrackDeletionSuccessNotification({ name: deletedName });
      } else if (ids.length > 1) {
        showTrackDeletionSuccessNotification({ count: ids.length });
      }
      // Their images were deleted with them
      void updateStorageSize();
      void fetchTracks();
    },
    [
      clearHistory,
      dispatch,
      fetchTracks,
      language,
      openErrorModal,
      t,
      updateStorageSize,
    ]
  );

  const createTrack = useCallback(
    async ({ color, routing, ...rest }: CreateTrackInterface) => {
      const fallbackColor =
        routing === Routing.none
          ? TRACK_COLORS.nonRoutable
          : TRACK_COLORS.routable;
      const newData = {
        routing,
        color: color ?? fallbackColor,
        src: "Trackbook",
        ...rest,
      };
      shouldSelectLastTrack.current = true;
      try {
        await updateTrack(-1, newData);
      } catch (error) {
        // Nothing was created, so the next tracks update mustn't select the last track
        shouldSelectLastTrack.current = false;
        throw error;
      }
      // API doesn't return anything when creating new track, so there's no simple way of selecting the new track based on the ID or sth
      // As a not so safe workaround, let's select the last one in the list
    },
    [updateTrack]
  );

  useEffect(() => {
    const lastTrack = tracks?.[tracks.length - 1];
    if (lastTrack && shouldSelectLastTrack.current) {
      // The edited track was saved as this new track, the editor keeps its data
      setSelectedTrackId(lastTrack.id);
      setOpenedTrack({ source: "own", track: lastTrack });
      shouldSelectLastTrack.current = false;
    }
  }, [setOpenedTrack, tracks]);

  // A public or shared track opened in the editor replaces the selected own track,
  // so the autosave never writes another track's data into it
  useEffect(() => {
    if (openedTrack && openedTrack.source !== "own") setSelectedTrackId(null);
  }, [openedTrack]);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setIsFetching(true);
      await fetchTracks();
      setIsFetching(false);
    };
    void fetch();
  }, [user, fetchTracks]);

  const selectedTrack = useMemo(() => {
    const foundTrack = tracks?.find((value) => value.id === selectedTrackId);
    // return a new copy to prevent accident original array mutation
    return foundTrack ? { ...foundTrack } : null;
  }, [selectedTrackId, tracks]);

  /**
   * Returns a list of images that are present in links in all waypoints
   */
  const getImagesFromLinks = async () => {
    const allLinks = tracks?.reduce<string[]>(
      (links, waypoint) => [...links, ...waypoint.links],
      []
    );
    if (!allLinks || allLinks.length === 0) return [];
    const imageUrls = await getUrlsIfAreValidImages(allLinks);
    return imageUrls;
  };

  const getSelectedTrackImages = useCallback(async () => {
    if (!selectedTrack || selectedTrack.links.length < 1) return [];
    const images = await getUrlsIfAreValidImages(selectedTrack.links);
    return images;
  }, [selectedTrack]);

  const leaveOpenTrack = useCallback(
    (leave: () => void) => {
      const openTrack = tracksRef.current?.find(
        (track) => track.id === selectedTrackIdRef.current
      );
      // While it's being saved, its latest change is already on the way
      const isSaved =
        !openTrack ||
        trackStatusRef.current === "saving" ||
        isEditedTrackSaved(routingStateRef.current, openTrack);
      if (isSaved) {
        leave();
        return true;
      }
      openConfirmLosingChangesModal({ onConfirm: leave });
      return false;
    },
    [openConfirmLosingChangesModal]
  );

  const toggleTrackVisibility = useCallback(
    (trackId: number) => {
      toggle(trackId, (id, visible) => updateTrack(id, { visible })).catch(() =>
        showErrorNotification({
          title: t("generic.error_title"),
          message: t(
            "track_and_waypoint_visibility.track_visibility_toggle_failure_text"
          ),
        })
      );
    },
    [t, toggle, updateTrack]
  );

  return {
    tracks,
    hiddenTrackIds,
    fetchTracks,
    selectedTrack,
    selectTrack,
    updateTrack,
    deleteTrack,
    createTrack,
    toggleTrackVisibility,
    leaveOpenTrack,
    isFetching,
    trackStatus,
    getImagesFromLinks,
    getSelectedTrackImages,
  };
};

export const useUserTracks = () => useContext(UserTrackContext);

export const UserTracksDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const userTrackData = useProvideUserTrackData();
  return (
    <UserTrackContext.Provider value={userTrackData}>
      {children}
    </UserTrackContext.Provider>
  );
};
